import * as React from "react"
import { array, object, string } from "yup"
import { useSignal } from "@preact/signals-react"
import { Grid, TextField } from "@mui/material"
import { useAppToast, useValidatorSeeker } from "@ui-seeker/app"
import { DrawerSeeker, SelectSeeker, TagFieldSeeker } from "@ui-seeker/core"
import { isEmpty } from '@ui-seeker/utils'

import { DIPCountry, DIPSite, DIPBrand, DIPSku, DIPSkuGroup, DIPSplitKpiSelect } from "@components"
import {BACKEND_APIS, GROUPED_BY, USER_ROLES, WEBBACK_V2_API} from "@constants"
import { useUserApp } from "@hooks"
import {
	CONDITION_OPTIONS,
	FREQUENCY_OPTIONS,
	REPORT_TYPES,
	STOCK_STATUS_OPTIONS,
	PRICES_PERCENTAGES_UNDER_REFERENCE,
	INCLUDE_NEW_PRODUCTS_OPTIONS, AMAZON_RELEASE_STATUS_OPTIONS, EMBARGO_STATUS_OPTIONS,
	OPTIONS_STREET_DATE
} from "./@constants"
import { useForm } from "./@hooks"
import { reportIDSelected, isOpen, rowSelected, formData } from "./@signals"
import {DIPSeller} from "../../../@components/CommonFilters/@components";


export default function ReportsPriceChangesDrawer() {

	const { showToast } = useAppToast()
	const { saveRow, rowFormatted } = useForm()
	const { userRole, userCustomer } = useUserApp()

	const validatorData = useValidatorSeeker({
		reportName: formData.value?.reportNameForm,
		emailCC: formData.value?.emailCC,
		sellers: formData.value?.sellers,
	}, object({
		reportName: string().required('Report name must be provided'),
		emailCC: array().min(1, 'At least 1 email is required'),
		...(formData.value?.reportTypeForm === REPORT_TYPES.SELLER_DEEP_DIVE) && {
			sellers: array()
				.min(1, 'At least 1 seller is required')
				.max(1, 'You can only choose 1 seller')
		}
	}))

	const drawerProps = {
		title: 'Edit Alert',
		isOpen: isOpen.value,
		controller: () => {
			rowSelected.value = {}
			isOpen.value = false
		},
		successProps: {
			onClick: isEmpty(validatorData.errors)
				? () => {
				const dataRow = rowFormatted(rowSelected.value)
				reportIDSelected.value = rowSelected.value?.reportID
				saveRow(dataRow)
				rowSelected.value = {}
				isOpen.value = false
			}
			: () => {
				showToast.warning('Please ensure all mandatory fields are completed')
			}
		}
	}

	const myProps = {
		country: {
			grid: {xs: 12, md: 12},
			multiple: true,
		},
		site: {
			grid: {xs: 12, md: 12}
		},
		brand: {
			grid: {xs: 12, md: 12}
		},
		skuGroup: {
			grid: { xs:12, md: 12}
		},
		sku: {
			grid: {xs: 12, md: 12}
		},
		emailCC: {
			grid: {xs: 12, md: 12}, 
			required: true,
			error: validatorData.errors?.emailCC
		},
		limitPercentage: {
			grid: {xs: 12, md: 12}
		},
		frequency: {
			grid: {xs: 12, md: 12}
		},
		splitKpi: {
			grid: {xs: 12, md: 12},
			excludeOptions: [GROUPED_BY.SKU, GROUPED_BY.SELLER, GROUPED_BY.COMPANY],
			required: true
		},
		sellers: {
			grid: {xs: 12, md: 12},
			error: validatorData.errors?.sellers
		},
		includeOnlyNewProducts: {
			grid: {xs: 12, md: 12}
		},
		status: {
			grid: {xs: 12, md: 12}
		},
	}

	const hasPriceUnderReference = !!formData.value?.conditions?.priceUnderReference

	return <DrawerSeeker {...drawerProps}>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<TextField
					fullWidth
					label="Report Name"
					size="small"
					value={formData.value?.reportNameForm}
					onChange={e => {
						formData.value = {...formData.value, reportNameForm: e.target.value}
					}}
					sx={{ mb: 3, mt: 3 }}
					helperText={validatorData.errors?.reportName}
					error={!!validatorData.errors?.reportName}
				/>
			</Grid>

			<Grid item {...myProps?.emailCC?.grid}>
				<TagFieldSeeker
					value={formData.value?.emailCC}
					onChange={(e) => {
						formData.value = { ...formData.value, emailCC: e }
					}}
					label="Email"
					placeholder="Enter to add emails"
					{...myProps?.emailCC}
				/>
			</Grid>

			<Grid item {...myProps?.frequency?.grid}>
				<SelectSeeker
					options={FREQUENCY_OPTIONS}
					value={formData.value?.frequencyForm}
					onChange={(e) => {
						formData.value = { ...formData.value, frequencyForm: e.target.value }
					}}
					label="Frequency"
					placeholder="Empty selector defaults to grouping by company."
					{...myProps?.frequency}
				/>
			</Grid>

			{formData.value?.reportTypeForm === REPORT_TYPES.PRICE_CHANGES && <Grid item {...myProps?.limitPercentage?.grid}>
				<SelectSeeker
					options={CONDITION_OPTIONS}
					value={formData.value?.conditions?.priceChange}
					onChange={(e) => {
						formData.value = {
							...formData.value,
							conditions: {...formData.value.conditions, 'priceChange': e.target.value}
						}
					}}
					label="Price Changes"
					{...myProps?.limitPercentage}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.PRICE_CHANGES && !!hasPriceUnderReference && <Grid item {...myProps?.limitPercentage?.grid}>
				<SelectSeeker
					options={[
						{label: 'Yes', value: 'true'},
						{label: 'No', value: 'false'},
					]}
					value={formData.value?.conditions?.priceUnderReference}
					onChange={(e) => {
						formData.value = {
							...formData.value,
							conditions: {...formData.value.conditions, 'priceUnderReference': e.target.value}
						}
					}}
					label="Only include prices under Reference"
					{...myProps?.limitPercentage}
				/>
			</Grid>}

			{!!hasPriceUnderReference && !!JSON.parse(formData.value?.conditions?.priceUnderReference) && <Grid item {...myProps?.limitPercentage?.grid}>
				<SelectSeeker
					label="Filter by % Below Reference?"
					fullWidth
					options={PRICES_PERCENTAGES_UNDER_REFERENCE}
					value={formData.value?.conditions?.percentagePriceUnder}
					onChange={(e) => {
						formData.value = {
							...formData.value,
							conditions: {...formData.value.conditions, 'percentagePriceUnder': e.target.value}
						}
					}}
					{...myProps?.limitPercentage}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.STOCK_CHANGES && <Grid item {...myProps?.limitPercentage?.grid}>
				<SelectSeeker
					options={STOCK_STATUS_OPTIONS}
					value={formData.value?.conditions?.finalStockStatus}
					multiple={true}
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {'finalStockStatus': e.target.value} }
					}}
					label="Final Stock Status"
					{...myProps?.limitPercentage}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.STOCK_DETAILS && userCustomer === 6 && <Grid item {...myProps?.limitPercentage?.grid}>
				<SelectSeeker
					options={OPTIONS_STREET_DATE}
					value={formData.value?.conditions?.streetDate || ''}
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {...formData.value.conditions, 'streetDate': e.target.value || null} }
					}}
					label="Filter by Street Date"
					isClearable
					{...myProps?.limitPercentage}
				/>
			</Grid>}

		{formData.value?.reportTypeForm === REPORT_TYPES.AMAZON_RELEASE_DATE || formData.value?.reportTypeForm === REPORT_TYPES.LOST_BUY_BOX_DETAILS && <Grid item {...myProps?.includeOnlyNewProducts?.grid}>
				<SelectSeeker
					options={INCLUDE_NEW_PRODUCTS_OPTIONS}
					value={formData.value?.conditions?.includeOnlyNewProducts}
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {...formData.value?.conditions, 'includeOnlyNewProducts': e.target.value} }
					}}
					label="Products to Include"
					{...myProps?.includeOnlyNewProducts}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.AMAZON_RELEASE_DATE && <Grid item {...myProps?.status?.grid}>
				<SelectSeeker
					options={AMAZON_RELEASE_STATUS_OPTIONS}
					value={formData.value?.conditions?.status}
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {...formData.value?.conditions, 'status': e.target.value} }
					}}
					label="Amazon Release Date Status"
					{...myProps?.status}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.EMBARGO_DATE  && <Grid item {...myProps?.includeOnlyNewProducts?.grid}>
				<SelectSeeker
					options={INCLUDE_NEW_PRODUCTS_OPTIONS}
					value={formData.value?.conditions?.includeOnlyNewProducts}
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {'includeOnlyNewProducts': e.target.value} }
					}}
					label="Products to Include"
					{...myProps?.includeOnlyNewProducts}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.EMBARGO_DATE  && <Grid item {...myProps?.status?.grid}>
				<SelectSeeker
					options={EMBARGO_STATUS_OPTIONS}
					value={formData.value?.conditions?.status || []}
					multiple
					onChange={(e) => {
						formData.value = { ...formData.value, conditions: {...formData.value?.conditions, 'status': e.target.value} }
					}}
					label="Embargo Status"
					{...myProps?.status}
				/>
			</Grid>}

			<Grid item {...myProps?.country?.grid}>
				<DIPCountry
					label="Market"
					placeholder="All Markets"
					value={formData.value?.countryForm}
					onChange={(e) => {
						formData.value = { ...formData.value, countryForm: e.target.value };
					}}
					{...myProps?.country}
				/>
			</Grid>
			
			<Grid item {...myProps?.site?.grid}>
				<DIPSite
					label="Retailer"
					placeholder="All Retailers"
					value={formData.value?.retailerForm}
					filters={{ country: formData.value?.countryForm}}
					onChange={(e) => {
						formData.value = { ...formData.value, retailerForm: e.target.value };
					}}
					{...myProps?.site}
				/>
			</Grid>

			<Grid item {...myProps?.brand?.grid}>
				<DIPBrand
					label="Brand"
					placeholder="Search Brand..."
					value={formData.value?.brandForm}
					onChange={(e) => {
						formData.value = { ...formData.value, brandForm: e.target.value };
					}}
					{...myProps?.brand}
				/>
			</Grid>

			<Grid item {...myProps?.skuGroup?.grid}>
				<DIPSkuGroup
					urlAjax={`${WEBBACK_V2_API}/master-data/sku-group/options`}
					label="SKU Group"
					value={formData.value?.skuGroupForm}
					placeholder="Select SKUs Group"
					onChange={(e) => {
						formData.value = { ...formData.value, skuGroupForm: e.target.value };
					}}
					{...myProps?.skuGroup}
				/>
        	</Grid>

			<Grid item {...myProps?.sku?.grid}>
				<DIPSku
					urlAjax={userRole === USER_ROLES.RETAILER ? `${BACKEND_APIS.MASTER_DATA}/sku/retailer-find` : `${WEBBACK_V2_API}/master-data/sku/find`}
					filters={{
						brand: formData.value.brandForm, retailer: formData.value.retailerForm, country: formData.value.countryForm,
						skuGroup: formData.value?.skuGroupForm
					}}
					label="SKU"
					placeholder="Search SKU..."
					value={formData.value?.skuForm}
					limitTags={4}
					onChange={(e) => {
						formData.value = { ...formData.value, skuForm: e.target.value };
					}}
					{...myProps?.sku}
				/>
        	</Grid>

			{formData.value?.reportTypeForm === REPORT_TYPES.DIGITAL_SHELF || formData.value?.reportTypeForm === REPORT_TYPES.PP_KPI && <Grid item {...myProps?.splitKpi?.grid}>
				<DIPSplitKpiSelect
					value={formData.value?.splitKpi}
					isClearable={false}
					onChange={(e) => {
						formData.value = { ...formData.value, splitKpi: e.target.value }
					}}
					{...myProps?.splitKpi}
				/>
			</Grid>}

			{formData.value?.reportTypeForm === REPORT_TYPES.SELLER_DEEP_DIVE && <Grid item {...myProps?.sellers?.grid}>
				<DIPSeller
					value={formData.value?.sellers}
					onChange={(e) => {
						formData.value = { ...formData.value, sellers: e.target.value }
					}}
					{...myProps?.sellers}
				/>
			</Grid>}

		</Grid>
	</DrawerSeeker>
}